import type { BackupApplyStatus } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CompanyForm } from "@/components/settings/CompanyForm";
import { GroupForm } from "@/components/settings/GroupForm";
import { Button } from "@/components/ui/button";
import { Card, PageHeader, Spinner } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { GROUP_TITLE, type GroupName } from "@/lib/settingsFields";

// Telegram guruhi hozircha yashirin (faqat veb ilova): sozlamasi backend'da saqlanadi, keyinroq yoqiladi
const ORDER: GroupName[] = ["general", "email", "storage", "sms", "hosting", "backup"];
const APPLY_ERRORS = {
  no_agent: "settings.apply_no_agent",
  incomplete: "settings.apply_incomplete",
} as const;

function BackupApply() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["settings", "backup-apply"],
    queryFn: async () => unwrap(await api.GET("/api/settings/backup/apply/")),
    refetchInterval: (q) => (q.state.data?.state === "pending" ? 3000 : false),
  });
  const apply = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/settings/backup/apply/")),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings", "backup-apply"] }),
    onError: (e) => {
      const code =
        e instanceof ApiError && e.body && typeof e.body === "object"
          ? (e.body as { code?: string }).code
          : undefined;
      toast.error(
        code && code in APPLY_ERRORS
          ? t(APPLY_ERRORS[code as keyof typeof APPLY_ERRORS])
          : errorMessage(e, t("common.error")),
      );
    },
  });
  const s: BackupApplyStatus | undefined = status.data;
  return (
    <div className="rounded-lg bg-muted p-3" data-testid="backup-apply">
      <p className="mb-2 text-sm text-muted-foreground">{t("settings.apply_hint")}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => apply.mutate()}
          disabled={apply.isPending || s?.state === "pending"}
        >
          {t("settings.apply")}
        </Button>
        {s?.state === "pending" && <span className="text-sm">{t("settings.apply_pending")}</span>}
        {s?.state === "ok" && (
          <span className="text-sm text-success">{t("settings.apply_ok")}</span>
        )}
        {s?.state === "failed" && (
          <span role="alert" className="text-sm text-danger">
            {t("settings.apply_failed", { msg: s.message })}
          </span>
        )}
      </div>
    </div>
  );
}

/** Faqat super_admin: firma, til, Telegram, SMS, email, hosting kaliti va zaxira joyi. Maxfiylar bazada shifrlangan. */
export function SettingsPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: async () => unwrap(await api.GET("/api/settings/")),
    retry: false,
  });
  const company = useQuery({
    queryKey: ["settings", "company"],
    queryFn: async () => unwrap(await api.GET("/api/settings/company/")),
  });

  const reload = () => void qc.invalidateQueries({ queryKey: ["settings"] });
  const save = async (values: Record<string, string | null>) => {
    unwrap(await api.PATCH("/api/settings/", { body: { values } }));
    reload();
  };
  const verify = async (group: GroupName, values: Record<string, string>) => {
    const r = unwrap(
      await api.POST("/api/settings/verify/{group}/", {
        params: { path: { group } },
        body: { values },
      }),
    );
    reload();
    return r;
  };

  if (settings.isError) {
    const code =
      settings.error instanceof ApiError &&
      settings.error.body &&
      typeof settings.error.body === "object"
        ? (settings.error.body as { code?: string }).code
        : undefined;
    return (
      <p role="alert" className="text-danger">
        {code === "encryption" ? t("settings.encryption_error") : t("common.error")}
      </p>
    );
  }
  if (!settings.data || company.isLoading) return <Spinner className="mx-auto my-10 size-8" />;
  const groups = settings.data.groups;
  const c = company.data && "id" in company.data ? company.data : null;

  return (
    <>
      <PageHeader title={t("settings.title")} />
      <div className="grid gap-4">
        <Card data-testid="company-card">
          <h2 className="mb-3 font-bold">{t("settings.company")}</h2>
          <CompanyForm
            key={c?.id ?? "new"}
            company={c}
            saveLabel={t("settings.save")}
            onSave={async (v) => {
              unwrap(await api.PATCH("/api/settings/company/", { body: v }));
              void qc.invalidateQueries({ queryKey: ["settings", "company"] });
            }}
            onUpload={async (file) => {
              const form = new FormData();
              form.append("file", file);
              unwrap(
                await api.POST("/api/settings/company/logo/", {
                  body: form as never,
                  bodySerializer: (b: unknown) => b as FormData,
                }),
              );
              void qc.invalidateQueries({ queryKey: ["settings", "company"] });
            }}
            onRemoveLogo={async () => {
              unwrap(await api.DELETE("/api/settings/company/logo/"));
              void qc.invalidateQueries({ queryKey: ["settings", "company"] });
            }}
          />
        </Card>
        {ORDER.map((g) => (
          <Card key={g} data-testid={`group-${g}`}>
            <h2 className="mb-3 font-bold">{t(GROUP_TITLE[g])}</h2>
            <GroupForm
              group={g}
              data={groups[g]!}
              onSave={save}
              onVerify={(values) => verify(g, values)}
              extra={g === "backup" ? <BackupApply /> : undefined}
            />
          </Card>
        ))}
      </div>
    </>
  );
}
